import { useMemo, useState } from 'react';

import { ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  SearchableDropdownMenu,
  SearchableMenuGroup,
} from '@/components/ui/custom/SearchableDropdownMenu/SearchableDropdownMenu';
import { cn } from '@/lib/utils';
import { getMetricById, getMetricsByCategory } from '@/modules/analytics/constants/metricsCatalog';

type Props = {
  value: string;
  onChange: (metricId: string) => void;
  placeholder: string;
  id?: string;
  ariaLabelledBy?: string;
  className?: string;
  disabled?: boolean;
};

export const MetricCombobox = ({
  value,
  onChange,
  placeholder,
  id,
  ariaLabelledBy,
  className,
  disabled,
}: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selectedCatalog = value ? getMetricById(value) : undefined;

  const menuGroups: SearchableMenuGroup[] = useMemo(
    () =>
      getMetricsByCategory().map(({ category, metrics }) => ({
        id: category.id,
        items: [
          {
            id: `cat:${category.id}`,
            label: category.label,
            searchText: category.label,
            children: metrics.map((metric) => ({
              id: metric.id,
              label: metric.label,
              searchText: metric.label,
              onClick: () => {
                onChange(metric.id);
                setOpen(false);
              },
            })),
          },
        ],
      })),
    [onChange]
  );

  return (
    <SearchableDropdownMenu
      isOpen={open}
      onOpenChange={setOpen}
      align="start"
      groups={menuGroups}
      searchPlaceholder={t('analytics.reports_detail_metric_search_placeholder')}
      emptyMessage={t('analytics.reports_detail_metric_search_empty')}
      trigger={
        <button
          type="button"
          id={id}
          aria-labelledby={ariaLabelledBy}
          disabled={disabled}
          className={cn(
            'group flex h-10 min-w-[156px] items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2.5 text-sm whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
        >
          <span className="line-clamp-1 text-left text-foreground">
            {selectedCatalog?.label ?? placeholder}
          </span>
          <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
        </button>
      }
    />
  );
};
